import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ListItem, UtilService } from '@material-library';
import { Subscription } from 'rxjs';
import { BrowserSupportService } from '../../../core/browser-support.service';
import { LinkFileListService } from '../../../link-consume/services/link-file-list.service';
import { BuildTransferItemService } from '../../../transfer/services/build-transfer-item.service';
import { IDirItem, ITransferItem, ITransferItemUpload, ITransferView, IUploadFolderView } from '../../../../sync';
import { TransferQueueType, TransferStatus } from '../../../transfer/models/transfer.model';
import { TransferService } from '../../../transfer/services/transfer.service';
import { compare } from '../../func';
import { BlendEvent, DownloadZip, FileItem, TransferItem } from '../../models';
import { FeatureService } from '../../services/feature.service';
import { TransferItemService } from '../../../transfer/services/transfer-item.service';
import { FileListService } from '../../../file-list/services/file-list.service';
import { RunUploadFolderService } from '../../../transfer/services/run-upload-folder.service';
import { DownloadZipService } from '../../../transfer/services/download-zip.service';
import { BlendService } from '../../services/blend.service';

@Component({
  selector: 'app-transfer',
  templateUrl: './transfer.component.html',
  styleUrls: ['./transfer.component.scss']
})
export class TransferComponent implements OnInit, OnDestroy {

  @Input() showFilter?: boolean = true;
  
  @Output() openFolder = new EventEmitter<{transferItem: TransferItem, id: number}>();
  @Output() previewFile= new EventEmitter<TransferItem>();
  
  public transferView: ITransferView;
  public foldertransferView: IUploadFolderView;
  public isPublic: number;
  private subscription: Subscription;
  private estimateSubscription: Subscription;
  private uploadable: boolean;
  public ufile: any;
  public isMinimized: boolean = false;
  public transferIndex: number = -1;
  public foldertransferIndex: number = -1;
  public downloadtransferIndex: number = -1;

  public filterBy: 'upload' | 'download' | 'all' = 'all';
  public estimatedTime: string;
  public isDisplayRetryButtonEventLogged = false;

  constructor(
    private buildTransferItemService: BuildTransferItemService,
    private linkFileListService: LinkFileListService,
    private browserSupportService: BrowserSupportService,
    private router: Router,
    private featureService: FeatureService,
    private transferService: TransferService,
    private transferItemService: TransferItemService,
    public utilService: UtilService,
    public fileListService: FileListService,
    private runUploadFolderService: RunUploadFolderService,
    public downloadZipService: DownloadZipService,
    private blendService: BlendService
  ) { }

  async ngOnInit() {
    this.transferView = this.buildTransferItemService.view;
    this.foldertransferView = this.runUploadFolderService.view;
    this.router.url.includes('/dl/') ? this.isPublic = 1 : this.isPublic = 0;

    this.subscription = this.linkFileListService
      .getSubscription()
      .subscribe((data) => {
        // do not check path list length as empty folder still needs to be uploadable
        if (data && data.pathlist && data.loaded && data.sorted) {
          this.uploadable = data.upload > 0;
        }
      });

    this.estimateSubscription = this.transferService.estimateTimeObservable.subscribe((estimatedTime: string) => {
      this.estimatedTime = estimatedTime;
    })
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.estimateSubscription) {
      this.estimateSubscription.unsubscribe();
    }
    this.downloadZipService.unsubscribeJob();
  }

  public get queueLength(): number {
    return [...this.transferView.upload.queue, ...this.transferView.download.queue, ...this.foldertransferView.rootItems, ...Object.values(this.downloadZipService.downloadZip)].filter(item => item.status !== TransferStatus.STATUS_UPLOAD_CANCELLED && item.status !== TransferStatus.STATUS_DOWNLOAD_CANCELLED).length;
  }

  public get hasError(): boolean {
    return [...this.transferView.upload.queue, ...this.transferView.download.queue].some(item => item.status > TransferStatus.STATUS_DOWNLOAD_CANCELLED) || this.foldertransferView.fileErrorCount > 1 || Object.values(this.downloadZipService.downloadZip).some(item => item.errcode);
  }

  public get filetransfersText(): string {
    const completed = [...this.transferView.upload.queue, ...this.transferView.download.queue, ...Object.values(this.downloadZipService.downloadZip)].filter(item => item.status === TransferStatus.STATUS_SUCCESS || item.status > TransferStatus.STATUS_DOWNLOAD_CANCELLED).length
    const completedFolders = this.foldertransferView.rootItems.filter(item => item.status === TransferStatus.STATUS_SUCCESS || item.status === TransferStatus.STATUS_ERROR).length;
    return `${completed + completedFolders} of ${this.queueLength}`;
  }

  public get zipDownloadQueue(): DownloadZip[] {
    return Object.values(this.downloadZipService.downloadZip)
    .sort((a, b) => a.created - b.created);
  }

  public testFileUpload(): boolean {
    return this.browserSupportService.testFileUpload();
  }
  public testDirUpload() {
    return this.browserSupportService.testDirUpload();
  }

  public showUploadBtn(): boolean {
    if (this.isPublic) {
      return this.uploadable;
    } else {
      return (
        this.router.url.includes('/files') ||
        this.router.url.includes('/vault')
      );
    }
  }

  public clear() {
    this.transferView.download.queue = this.transferView.download.queue.filter(
      (transferItem) =>
        (transferItem.status != 1 && transferItem.status <= 1000) ||
        ![10, 20].includes(transferItem.type)
    );

    this.transferView.upload.queue = this.transferView.upload.queue.filter(
      (transferItem) =>
        (transferItem.status != 1 && transferItem.status <= 1000) ||
        ![10, 20].includes(transferItem.type)
    );
  }

  public downloadCompatible(file: ITransferItem) {
    this.buildTransferItemService
      .getCompatUrl(file as any, this.buildTransferItemService.ACT_DOWNLOAD)
      .then((data) => {
        window.location.href = data;
        file.status = TransferStatus.STATUS_SUCCESS;
      });
  }

  public sort(a: TransferItem, b: TransferItem) {
    return compare(a._initTime, b._initTime, true);
  }

  public uploadFileSelect(event: any) {
    this.ufile = event.target.files;
  }

  public cancelUpload(transferItem: TransferItem) {
    if (transferItem.status >= 10 && transferItem.status <= 20) {
      transferItem.status = TransferStatus.STATUS_UPLOAD_CANCELLED;
      const processingCount =  this.transferView.upload.queue.filter(item => item.status !== TransferStatus.STATUS_UPLOAD_CANCELLED).length;
      if (processingCount === 0) {
        this.transferView.upload.isProcessing = false;
      }
      if (this.transferView.upload.queue.length > 0) {
        this.buildTransferItemService.runQueue(TransferQueueType.UPLOAD);
      }
    } else {
      transferItem.status = TransferStatus.STATUS_UPLOAD_CANCELLED;
    }
  }
  public cancelFolderUpload(transferItem: IDirItem) {
    if (transferItem.status === TransferStatus.STATUS_WAITING) {
      transferItem.status = TransferStatus.STATUS_UPLOAD_CANCELLED;
      for (let i = 0, len = this.runUploadFolderService.workFiles.length; i < len; i++) {
        const tItem = this.runUploadFolderService.workFiles[i];
        const rootPath = tItem.fullPath.split('/').slice(0, 2).join('/');
        if (rootPath === transferItem.fullPath) {
          this.runUploadFolderService.workFiles[i].status = TransferStatus.STATUS_UPLOAD_CANCELLED;
        }
      }
    }
  }

  public cancelDownload(transferItem: TransferItem) {
    if (transferItem.status >= 10 && transferItem.status <= 20) {
      transferItem.status = TransferStatus.STATUS_DOWNLOAD_CANCELLED;
      const processingCount =  this.transferView.download.queue.filter(item => item.status !== TransferStatus.STATUS_DOWNLOAD_CANCELLED).length;
      if (processingCount === 0) {
        this.transferView.download.isProcessing = false;
      }
      if (this.transferView.download.queue.length > 0) {
        this.buildTransferItemService.runQueue(TransferQueueType.DOWNLOAD);
      }
    } else {
      transferItem.status = TransferStatus.STATUS_DOWNLOAD_CANCELLED;
    }
  }

  public shouldShowRetryButton(item: ITransferItemUpload, index: number): boolean {
    const shouldShow =
      this.transferIndex === index &&
      item.retryCount < 3 &&
      item.status >= 7000 &&
      item.status <= 7244;
  
    if (shouldShow && !this.isDisplayRetryButtonEventLogged) {
      this.blendService.track(BlendEvent.RETRY_UPLOAD_BUTTON_DISPLAYED);
      this.isDisplayRetryButtonEventLogged = true;
    }
  
    return shouldShow;
  }

  public async retryUpload(transferItem: ITransferItemUpload) {
    if (transferItem.retryCount < 3) {
      if (transferItem.status >= 7000 && transferItem.status <= 7244) {
        transferItem.chunkqueue = this.transferItemService.getChunkQueue(transferItem);
        transferItem.bytes_sent = 0;
        transferItem.progress_percent = 0;
        transferItem.progress_network = 0;
        transferItem.progress_crypt = 0;
        transferItem.status = TransferStatus.STATUS_WAITING;
        await this.buildTransferItemService.runQueue(TransferQueueType.UPLOAD);
        this.blendService.track(BlendEvent.BUTTON_CLICKED, {button_text: 'Retry upload'});
      }
      ++transferItem.retryCount;
    }
  }

  public previewItem(transferItem: TransferItem) {
    this.previewFile.emit(transferItem);
  }

  public handlePreviewFolder(transferItem: IDirItem | ITransferItem) {
    this.openFolder.emit({transferItem, id: transferItem.id});
  }

  public handleOpenFolder(transferItem: TransferItem) {
    this.openFolder.emit({transferItem, id: transferItem.sync_pid});
  }

  public trackbyFunction(index) {
    return index;
  }

  public onAllFilter = () => {
    this.filterBy = 'all';
  }
  public onUploadFilter = () => {
    this.filterBy = 'upload';
  }
  public onDownloadFilter = () => {
    this.filterBy = 'download';
  }
  public onCloseTransfer = () => {
    this.transferService.closeTransfer();
    this.buildTransferItemService.resetView();
    this.runUploadFolderService.reset();
    this.downloadZipService.reset();
  }
  public onMinimizeToggle = () => {
    this.transferView.isMinimized = !this.transferView.isMinimized;
  }

}
